import { useState } from 'react';
import { LoaderCircle, MapPin, Minus, Plus, Store, Trash2, Truck } from 'lucide-react';
import { load as loadCashfree } from '@cashfreepayments/cashfree-js';

import { ApiError } from '@/api/client';
import { patientApi } from '@/api/patientClient';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/use-toast';
import { usePatientSession } from './patientSession';
import { LoginDialog } from './LoginDialog';
import { useCart } from './cart';
import type { DeliveryMethod, PlaceOrderRequest, PlaceOrderResponse } from './types';

function money(n: number) {
  return `₹${n.toFixed(2)}`;
}

export function CartDrawer({
  pharmacySlug,
  open,
  onClose,
  deliveryFee,
  minOrderAmount,
}: {
  pharmacySlug: string;
  open: boolean;
  onClose: () => void;
  deliveryFee: number;
  minOrderAmount: number;
}) {
  const { isAuthenticated } = usePatientSession();
  const cart = useCart(pharmacySlug);

  const [method, setMethod] = useState<DeliveryMethod>('Pickup');
  const [address, setAddress] = useState('');
  const [loginOpen, setLoginOpen] = useState(false);
  const [placing, setPlacing] = useState(false);

  const fee = method === 'Delivery' ? deliveryFee : 0;
  const total = cart.subtotal + cart.gstAmount + fee;
  const belowMin = total < minOrderAmount;

  const placeOrder = async () => {
    if (method === 'Delivery' && !address.trim()) {
      toast({ title: 'Delivery address required', variant: 'destructive' });
      return;
    }
    if (!isAuthenticated) {
      setLoginOpen(true);
      return;
    }

    setPlacing(true);
    try {
      const body: PlaceOrderRequest = {
        items: cart.lines.map((l) => ({ medicineId: l.medicineId, quantity: l.quantity })),
        deliveryMethod: method,
        deliveryAddress: method === 'Delivery' ? address.trim() : undefined,
      };
      const order = await patientApi.post<PlaceOrderResponse>('/api/patient-orders', body);

      const cashfree = await loadCashfree({ mode: order.cashfreeIsLive ? 'production' : 'sandbox' });
      if (!cashfree) {
        toast({ title: 'Could not load the payment page', description: 'Please try again.', variant: 'destructive' });
        return;
      }

      cart.clear();
      // redirectTarget '_self': the whole tab navigates to Cashfree's hosted
      // checkout, then back to our own returnUrl once payment settles — the
      // backend already builds that return_url, no route param needed here.
      // On success this navigates away before the promise below ever
      // resolves; it only resolves here if something failed *before* the
      // redirect could happen (bad session id, blocked popup, etc).
      const result = await cashfree.checkout({ paymentSessionId: order.cashfreePaymentSessionId, redirectTarget: '_self' });
      if (result.error) {
        toast({ title: 'Could not open the payment page', description: result.error.message, variant: 'destructive' });
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'BELOW_MIN_ORDER' || err.code === 'INSUFFICIENT_STOCK' || err.code === 'ITEM_UNAVAILABLE') {
          toast({ title: err.message, variant: 'destructive' });
        } else if (err.code === 'CASHFREE_NOT_CONFIGURED' || err.code === 'ORDERING_DISABLED') {
          toast({ title: 'Online ordering is unavailable right now', description: err.message, variant: 'destructive' });
        } else {
          toast({ title: 'Could not place your order', description: err.message, variant: 'destructive' });
        }
      } else {
        toast({ title: 'Something went wrong', variant: 'destructive' });
      }
    } finally {
      setPlacing(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
        <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Your order</DialogTitle>
          </DialogHeader>

          {cart.lines.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Your cart is empty.</p>
          ) : (
            <>
              <div className="-mx-1 flex-1 space-y-3 overflow-y-auto px-1">
                {cart.lines.map((line) => (
                  <div key={line.medicineId} className="flex items-center gap-3 rounded-xl border border-border/60 p-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{line.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {money(line.price)} / {line.unit}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => cart.setQuantity(line.medicineId, line.quantity - 1)}
                        className="flex h-7 w-7 items-center justify-center rounded-full border border-border/60 hover:bg-secondary"
                        aria-label="Decrease quantity"
                      >
                        <Minus className="h-3.5 w-3.5" />
                      </button>
                      <span className="w-6 text-center text-sm font-medium tabular-nums">{line.quantity}</span>
                      <button
                        onClick={() => cart.setQuantity(line.medicineId, line.quantity + 1)}
                        className="flex h-7 w-7 items-center justify-center rounded-full border border-border/60 hover:bg-secondary"
                        aria-label="Increase quantity"
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <button
                      onClick={() => cart.removeItem(line.medicineId)}
                      className="text-muted-foreground hover:text-destructive"
                      aria-label={`Remove ${line.name}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>

              <div className="space-y-3 border-t border-border/60 pt-4">
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setMethod('Pickup')}
                    className={`flex items-center justify-center gap-2 rounded-xl border py-2.5 text-sm font-medium transition-colors ${
                      method === 'Pickup' ? 'border-primary bg-primary/10 text-primary' : 'border-border/60 text-muted-foreground'
                    }`}
                  >
                    <Store className="h-4 w-4" /> Store pickup
                  </button>
                  <button
                    onClick={() => setMethod('Delivery')}
                    className={`flex items-center justify-center gap-2 rounded-xl border py-2.5 text-sm font-medium transition-colors ${
                      method === 'Delivery' ? 'border-primary bg-primary/10 text-primary' : 'border-border/60 text-muted-foreground'
                    }`}
                  >
                    <Truck className="h-4 w-4" /> Delivery
                  </button>
                </div>

                {method === 'Delivery' && (
                  <div className="space-y-1.5">
                    <Label htmlFor="delivery-address">Delivery address</Label>
                    <div className="relative">
                      <MapPin className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="delivery-address"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        className="pl-9"
                        placeholder="House no, street, landmark"
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-1 text-sm">
                  <div className="flex justify-between text-muted-foreground">
                    <span>Subtotal</span>
                    <span>{money(cart.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>GST</span>
                    <span>{money(cart.gstAmount)}</span>
                  </div>
                  {method === 'Delivery' && (
                    <div className="flex justify-between text-muted-foreground">
                      <span>Delivery fee</span>
                      <span>{money(fee)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-1 text-base font-semibold">
                    <span>Total</span>
                    <span>{money(total)}</span>
                  </div>
                </div>

                {belowMin && (
                  <p className="text-xs text-destructive">Minimum order amount is {money(minOrderAmount)}.</p>
                )}

                <Button className="w-full" size="lg" disabled={placing || belowMin} onClick={placeOrder}>
                  {placing ? <LoaderCircle className="h-4 w-4 animate-spin" /> : `Place order · ${money(total)}`}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <LoginDialog
        pharmacySlug={pharmacySlug}
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        onSuccess={() => {
          setLoginOpen(false);
          void placeOrder();
        }}
      />
    </>
  );
}
